/**
 * Profile settings UI: TTS, Appearance, Shortcuts, Account panels.
 * Used by the full settings page (sidebar + panels) and optionally by the legacy modal block.
 */

import { createEl } from '../core/dom';
import { getSetting, setSetting, HIGHLIGHT_COLORS } from '../settings';
import type { AuthService, LecturaUser } from '../core/types';

/** Icons for settings nav (inline SVG). */
export const NAV_ICONS: Record<string, string> = {
  appearance:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="13.5" cy="6.5" r="1.5" fill="currentColor" stroke="none"/><circle cx="17.5" cy="10.5" r="1.5" fill="currentColor" stroke="none"/><circle cx="8.5" cy="7.5" r="1.5" fill="currentColor" stroke="none"/><circle cx="6.5" cy="12.5" r="1.5" fill="currentColor" stroke="none"/><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.9 0 1.7-.7 1.7-1.7 0-.4-.2-.8-.4-1.1-.3-.3-.4-.7-.4-1.1 0-.9.7-1.7 1.7-1.7H16c3.3 0 6-2.7 6-6 0-5.2-4.5-8.4-10-8.4z"/></svg>',
  presentation:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 5h16v10H4z"/><path d="M8 19h8"/><path d="M12 15v4"/><path d="M9 9h6"/><path d="M9 12h3"/></svg>',
  tts: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/></svg>',
  shortcuts:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M7 9h.01"/><path d="M11 9h6"/><path d="M7 13h.01"/><path d="M11 13h3"/><path d="M7 17h10"/></svg>',
  account: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>'
};

const TTS_LANGS = [
  { value: 'en', label: 'English' },
  { value: 'nl', label: 'Nederlands' },
  { value: 'de', label: 'Deutsch' },
  { value: 'fr', label: 'Français' },
  { value: 'es', label: 'Español' }
];

export function applyHighlightColorToDOM(rgb: string): void {
  if (!rgb || typeof document === 'undefined') return;
  document.documentElement.style.setProperty('--notes-tts-highlight-rgb', rgb);
  const reveal = document.querySelector('.reveal');
  if (reveal) (reveal as HTMLElement).style.setProperty('--notes-tts-highlight-rgb', rgb);
}

/** Apply highlight from local/account settings (appearance/tts highlightColorId). Used on remote shell where there is no .reveal. */
export function applyHighlightFromStoredSettings(): void {
  const id =
    String(getSetting('appearance', 'highlightColorId') || getSetting('tts', 'highlightColorId') || '').trim() || 'purple';
  const rgb = HIGHLIGHT_COLORS.find((c) => c.id === id)?.rgb;
  if (rgb) applyHighlightColorToDOM(rgb);
}

/** Build only the Appearance panel content (highlight color). */
export function buildAppearancePanelContent(container: HTMLElement): void {
  const colorRow = createEl('div', {
    className:
      'flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between rounded-2xl border border-white/10 bg-white/[0.02] p-4'
  });
  const colorLabel = createEl('div', { className: 'text-sm font-semibold text-white/90' });
  colorLabel.textContent = 'Highlight color';
  colorRow.appendChild(colorLabel);
  const swatches = createEl('div', { className: 'flex flex-wrap gap-2' });
  const currentColorId = (getSetting('appearance', 'highlightColorId') as string) ?? 'purple';
  HIGHLIGHT_COLORS.forEach((c) => {
    const btn = createEl('button', {
      type: 'button',
      className:
        'h-9 w-9 rounded-full border border-white/10 shadow-inner transition hover:scale-[1.02] active:scale-[0.98]' +
        (c.id === currentColorId ? ' ring-2 ring-amber-300/60 ring-offset-2 ring-offset-slate-950' : ''),
      'aria-label': c.label,
      title: c.label
    });
    btn.style.backgroundColor = `rgb(${c.rgb})`;
    btn.addEventListener('click', () => {
      setSetting('tts', 'highlightColorId', c.id);
      setSetting('appearance', 'highlightColorId', c.id);
      applyHighlightColorToDOM(c.rgb);
      swatches
        .querySelectorAll('button')
        .forEach((s) =>
          s.classList.remove('ring-2', 'ring-amber-300/60', 'ring-offset-2', 'ring-offset-slate-950')
        );
      btn.classList.add('ring-2', 'ring-amber-300/60', 'ring-offset-2', 'ring-offset-slate-950');
    });
    swatches.appendChild(btn);
  });
  colorRow.appendChild(swatches);
  container.appendChild(colorRow);
}

/** Build only the TTS panel content (lang, voice, rate, volume, toggles). */
export function buildTTSPanelContent(container: HTMLElement): void {
  const form = createEl('div', { className: 'grid gap-4' });

  const langRow = createEl('div', {
    className: 'grid gap-2 rounded-2xl border border-white/10 bg-white/[0.02] p-4'
  });
  langRow.innerHTML = '<label class="text-sm font-semibold text-white/90" for="rf-settings-lang-page">Language</label>';
  const langSelect = createEl('select', {
    className: 'rf-settings-page-select',
    id: 'rf-settings-lang-page'
  });
  TTS_LANGS.forEach(({ value, label }) => {
    const opt = document.createElement('option');
    opt.value = value;
    opt.textContent = label;
    langSelect.appendChild(opt);
  });
  langRow.appendChild(langSelect);
  form.appendChild(langRow);

  const voiceRow = createEl('div', {
    className: 'grid gap-2 rounded-2xl border border-white/10 bg-white/[0.02] p-4'
  });
  voiceRow.innerHTML = '<label class="text-sm font-semibold text-white/90" for="rf-settings-voice-page">Voice</label>';
  const voiceSelect = createEl('select', {
    className: 'rf-settings-page-select',
    id: 'rf-settings-voice-page'
  });
  voiceSelect.innerHTML = '<option value="">Default</option>';
  voiceRow.appendChild(voiceSelect);
  form.appendChild(voiceRow);

  function fillVoices(): void {
    const voices = typeof speechSynthesis !== 'undefined' ? speechSynthesis.getVoices() : [];
    const current = String(getSetting('tts', 'voice') || '').trim();
    const first = voiceSelect.querySelector('option');
    voiceSelect.innerHTML = '';
    if (first) voiceSelect.appendChild(first);
    voices.forEach((v) => {
      const opt = document.createElement('option');
      opt.value = v.voiceURI || v.name;
      opt.textContent = v.name + (v.lang ? ` (${v.lang})` : '');
      if (current && (v.voiceURI === current || v.name === current)) opt.selected = true;
      voiceSelect.appendChild(opt);
    });
  }
  fillVoices();
  if (typeof speechSynthesis !== 'undefined' && speechSynthesis.onvoiceschanged) {
    speechSynthesis.onvoiceschanged = fillVoices;
  }

  const rateRow = createEl('div', {
    className: 'grid gap-3 rounded-2xl border border-white/10 bg-white/[0.02] p-4'
  });
  rateRow.innerHTML =
    '<div class="flex items-center justify-between gap-4">' +
    '<label class="text-sm font-semibold text-white/90" for="rf-settings-rate-page">Speed</label>' +
    '</div>';
  const rateVal = createEl('span', { className: 'text-xs font-semibold text-white/60' });
  const rate = Number((getSetting('tts', 'rate') as number) ?? 0.9);
  rateVal.textContent = `${rate.toFixed(1)}×`;
  const rateInput = createEl('input', {
    type: 'range',
    className: 'w-full accent-amber-300',
    id: 'rf-settings-rate-page',
    min: '0.5',
    max: '1.5',
    step: '0.1',
    value: String(rate)
  }) as HTMLInputElement;
  rateInput.addEventListener('input', () => {
    const v = Number(rateInput.value || 0.9);
    setSetting('tts', 'rate', v);
    rateVal.textContent = `${v.toFixed(1)}×`;
  });
  (rateRow.firstElementChild as HTMLElement | null)?.appendChild(rateVal);
  rateRow.appendChild(rateInput);
  form.appendChild(rateRow);

  const volumeRow = createEl('div', {
    className: 'grid gap-3 rounded-2xl border border-white/10 bg-white/[0.02] p-4'
  });
  volumeRow.innerHTML =
    '<div class="flex items-center justify-between gap-4">' +
    '<label class="text-sm font-semibold text-white/90" for="rf-settings-volume-page">Volume</label>' +
    '</div>';
  const volumeVal = createEl('span', { className: 'text-xs font-semibold text-white/60' });
  const volume = Number((getSetting('tts', 'volume') as number) ?? 1);
  volumeVal.textContent = `${Math.round(volume * 100)}%`;
  const volumeInput = createEl('input', {
    type: 'range',
    className: 'w-full accent-amber-300',
    id: 'rf-settings-volume-page',
    min: '0',
    max: '1',
    step: '0.1',
    value: String(volume)
  }) as HTMLInputElement;
  volumeInput.addEventListener('input', () => {
    const v = Number(volumeInput.value || 1);
    setSetting('tts', 'volume', v);
    volumeVal.textContent = `${Math.round(v * 100)}%`;
  });
  (volumeRow.firstElementChild as HTMLElement | null)?.appendChild(volumeVal);
  volumeRow.appendChild(volumeInput);
  form.appendChild(volumeRow);

  const subtitlesRow = createEl('div', {
    className:
      'flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-4'
  });
  const subtitlesText = createEl('div', { className: 'min-w-0' });
  subtitlesText.innerHTML =
    '<p class="text-sm font-semibold text-white/90">Subtitles</p>' +
    '<p class="mt-1 text-xs leading-5 text-white/55">Show captions while playing voiceover.</p>';
  subtitlesRow.appendChild(subtitlesText);
  const subtitlesLabel = createEl('label', {
    className: 'relative inline-flex items-center cursor-pointer select-none',
    for: 'rf-settings-subtitles-page'
  });
  const subtitlesCheck = createEl('input', {
    type: 'checkbox',
    className: 'sr-only peer',
    id: 'rf-settings-subtitles-page'
  }) as HTMLInputElement;
  subtitlesCheck.checked = (getSetting('tts', 'subtitles') as boolean) ?? false;
  subtitlesCheck.addEventListener('change', () => setSetting('tts', 'subtitles', subtitlesCheck.checked));
  const subtitlesSwitch = createEl('span', {
    className:
      'h-6 w-11 rounded-full border border-white/10 bg-white/10 transition peer-focus:outline-none ' +
      'peer-focus:ring-2 peer-focus:ring-amber-300/20 peer-checked:bg-amber-300/30 peer-checked:border-amber-300/30 ' +
      "after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white/90 after:transition after:shadow " +
      'peer-checked:after:translate-x-5'
  });
  subtitlesLabel.appendChild(subtitlesCheck);
  subtitlesLabel.appendChild(subtitlesSwitch);
  subtitlesRow.appendChild(subtitlesLabel);
  form.appendChild(subtitlesRow);

  const autoAdvanceRow = createEl('div', {
    className:
      'flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-4'
  });
  const autoAdvanceText = createEl('div', { className: 'min-w-0' });
  autoAdvanceText.innerHTML =
    '<p class="text-sm font-semibold text-white/90">Auto-advance slides</p>' +
    '<p class="mt-1 text-xs leading-5 text-white/55">Move to the next slide automatically after narration.</p>';
  autoAdvanceRow.appendChild(autoAdvanceText);
  const autoAdvanceLabel = createEl('label', {
    className: 'relative inline-flex items-center cursor-pointer select-none',
    for: 'rf-settings-auto-advance-page'
  });
  const autoAdvanceCheck = createEl('input', {
    type: 'checkbox',
    className: 'sr-only peer',
    id: 'rf-settings-auto-advance-page'
  }) as HTMLInputElement;
  autoAdvanceCheck.checked = (getSetting('tts', 'autoAdvance') as boolean) ?? false;
  autoAdvanceCheck.addEventListener('change', () => setSetting('tts', 'autoAdvance', autoAdvanceCheck.checked));
  const autoAdvanceSwitch = createEl('span', {
    className:
      'h-6 w-11 rounded-full border border-white/10 bg-white/10 transition peer-focus:outline-none ' +
      'peer-focus:ring-2 peer-focus:ring-amber-300/20 peer-checked:bg-amber-300/30 peer-checked:border-amber-300/30 ' +
      "after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white/90 after:transition after:shadow " +
      'peer-checked:after:translate-x-5'
  });
  autoAdvanceLabel.appendChild(autoAdvanceCheck);
  autoAdvanceLabel.appendChild(autoAdvanceSwitch);
  autoAdvanceRow.appendChild(autoAdvanceLabel);
  form.appendChild(autoAdvanceRow);

  langSelect.value = String(getSetting('tts', 'lang') || 'en').trim().toLowerCase().substring(0, 2) || 'en';
  langSelect.addEventListener('change', () => {
    setSetting('tts', 'lang', langSelect.value);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('lectura-tts-lang-changed', { detail: { lang: langSelect.value } }));
    }
    fillVoices();
  });
  voiceSelect.addEventListener('change', () => {
    setSetting('tts', 'voice', voiceSelect.value || null);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('lectura-tts-voice-changed'));
    }
  });

  container.appendChild(form);
}

const SHORTCUTS_HTML =
  '<div class="rounded-2xl border border-white/10 bg-white/[0.02] p-4">' +
  '<p class="flex items-center justify-between gap-4 text-xs text-white/55">' +
  '<span><kbd class="rounded-md border border-white/10 bg-white/5 px-1.5 py-0.5 text-[0.7rem]">+</kbd> press together</span>' +
  '<span><kbd class="rounded-md border border-white/10 bg-white/5 px-1.5 py-0.5 text-[0.7rem]">/</kbd> alternatives</span>' +
  '</p>' +
  '<div class="mt-4 grid gap-2 text-sm">' +
  '<div class="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2"><span class="flex items-center gap-1 text-white/85"><kbd class="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[0.75rem]">N</kbd><span class="text-white/35">/</span><kbd class="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[0.75rem]">Space</kbd></span><span class="text-white/60">Next slide</span></div>' +
  '<div class="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2"><span class="flex items-center gap-1 text-white/85"><kbd class="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[0.75rem]">P</kbd><span class="text-white/35">/</span><kbd class="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[0.75rem]">Shift</kbd><span class="text-white/35">+</span><kbd class="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[0.75rem]">Space</kbd></span><span class="text-white/60">Previous slide</span></div>' +
  '<div class="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2"><span class="flex items-center gap-1 text-white/85"><kbd class="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[0.75rem]">←</kbd><span class="text-white/35">/</span><kbd class="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[0.75rem]">H</kbd></span><span class="text-white/60">Navigate left</span></div>' +
  '<div class="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2"><span class="flex items-center gap-1 text-white/85"><kbd class="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[0.75rem]">→</kbd><span class="text-white/35">/</span><kbd class="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[0.75rem]">L</kbd></span><span class="text-white/60">Navigate right</span></div>' +
  '<div class="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2"><span class="flex items-center gap-1 text-white/85"><kbd class="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[0.75rem]">↑</kbd><span class="text-white/35">/</span><kbd class="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[0.75rem]">K</kbd></span><span class="text-white/60">Navigate up</span></div>' +
  '<div class="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2"><span class="flex items-center gap-1 text-white/85"><kbd class="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[0.75rem]">↓</kbd><span class="text-white/35">/</span><kbd class="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[0.75rem]">J</kbd></span><span class="text-white/60">Navigate down</span></div>' +
  '<div class="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2"><span class="flex items-center gap-1 text-white/85"><kbd class="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[0.75rem]">Alt</kbd><span class="text-white/35">+</span><kbd class="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[0.75rem]">←/↑/→/↓</kbd></span><span class="text-white/60">Navigate without fragments</span></div>' +
  '<div class="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2"><span class="flex items-center gap-1 text-white/85"><kbd class="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[0.75rem]">Shift</kbd><span class="text-white/35">+</span><kbd class="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[0.75rem]">←/↑/→/↓</kbd></span><span class="text-white/60">Jump to first/last slide in direction</span></div>' +
  '<div class="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2"><span class="flex items-center gap-1 text-white/85"><kbd class="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[0.75rem]">Esc</kbd><span class="text-white/35">/</span><kbd class="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[0.75rem]">O</kbd></span><span class="text-white/60">Slide overview</span></div>' +
  '<div class="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2"><span class="flex items-center gap-1 text-white/85"><kbd class="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[0.75rem]">B</kbd><span class="text-white/35">/</span><kbd class="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[0.75rem]">.</kbd></span><span class="text-white/60">Pause the presentation</span></div>' +
  '<div class="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2"><span class="flex items-center gap-1 text-white/85"><kbd class="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[0.75rem]">F</kbd></span><span class="text-white/60">Toggle fullscreen</span></div>' +
  '<div class="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2"><span class="flex items-center gap-1 text-white/85"><kbd class="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[0.75rem]">G</kbd></span><span class="text-white/60">Jump to slide number</span></div>' +
  '<div class="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2"><span class="flex items-center gap-1 text-white/85"><kbd class="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[0.75rem]">S</kbd></span><span class="text-white/60">Toggle remote QR overlay</span></div>' +
  '<div class="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2"><span class="flex items-center gap-1 text-white/85"><kbd class="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[0.75rem]">Ctrl</kbd><span class="text-white/35">+</span><kbd class="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[0.75rem]">Shift</kbd><span class="text-white/35">+</span><kbd class="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[0.75rem]">F</kbd></span><span class="text-white/60">Search</span></div>' +
  '<div class="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2"><span class="flex items-center gap-1 text-white/85"><kbd class="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[0.75rem]">Alt</kbd><span class="text-white/35">+</span><kbd class="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[0.75rem]">Click</kbd></span><span class="text-white/60">Zoom into slide element</span></div>' +
  '<div class="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2"><span class="flex items-center gap-1 text-white/85"><kbd class="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[0.75rem]">R</kbd></span><span class="text-white/60">Toggle extra resources</span></div>' +
  '<div class="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2"><span class="flex items-center gap-1 text-white/85"><kbd class="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[0.75rem]">C</kbd></span><span class="text-white/60">Toggle chat drawer</span></div>' +
  '<div class="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2"><span class="flex items-center gap-1 text-white/85"><kbd class="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[0.75rem]">T</kbd></span><span class="text-white/60">Start/stop notes voiceover</span></div>' +
  '<div class="flex items-center justify-between gap-4 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2"><span class="flex items-center gap-1 text-white/85"><kbd class="rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[0.75rem]">?</kbd></span><span class="text-white/60">Open shortcuts panel</span></div>' +
  '</div>' +
  '</div>';

/** Build Shortcuts panel content (append to panel which already has head). */
export function buildShortcutsPanelContent(panel: HTMLElement): void {
  panel.insertAdjacentHTML('beforeend', SHORTCUTS_HTML);
}

export interface BuildAccountPanelOptions {
  user: LecturaUser | null;
  authService: AuthService;
  slidesVersion: string;
  onClose: () => void;
}

function escapeHtml(s: string): string {
  const div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}

const GOOGLE_ICON =
  '<svg viewBox="0 0 24 24" width="18" height="18"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>';
const GITHUB_ICON =
  '<svg viewBox="0 0 24 24" width="18" height="18" fill="#fff"><path d="M12 .3a12 12 0 0 0-3.8 23.38c.6.12.83-.26.83-.57V20.58c-3.34.73-4.04-1.42-4.04-1.42-.55-1.39-1.34-1.76-1.34-1.76-1.08-.74.08-.73.08-.73 1.2.09 1.84 1.24 1.84 1.24 1.07 1.83 2.8 1.3 3.49 1 .1-.78.42-1.3.76-1.6-2.67-.31-5.47-1.34-5.47-5.93 0-1.31.47-2.38 1.24-3.22-.13-.3-.54-1.52.12-3.18 0 0 1-.32 3.3 1.23a11.5 11.5 0 0 1 6.02 0c2.28-1.55 3.29-1.23 3.29-1.23.66 1.66.25 2.88.12 3.18a4.65 4.65 0 0 1 1.24 3.22c0 4.6-2.8 5.62-5.48 5.92.43.37.81 1.1.81 2.22v3.29c0 .31.22.69.82.57A12 12 0 0 0 12 .3z"/></svg>';

/** Build Account panel content (linked providers, privacy, Sign out, Remove account; or guest sign-in). */
export function buildAccountPanelContent(container: HTMLElement, opts: BuildAccountPanelOptions): void {
  const { user, authService, onClose } = opts;
  const isGuest = !user || !!user.isAnonymous;

  const GOOGLE_BTN_SVG =
    '<svg class="rf-login-icon-svg" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.03 24.03 0 0 0 0 21.56l7.98-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>';
  const GITHUB_BTN_SVG =
    '<svg class="rf-login-icon-svg" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z"/></svg>';
  if (isGuest) {
    container.innerHTML =
      '<div class="rounded-2xl border border-white/10 bg-white/[0.02] p-4">' +
      '<p class="text-sm leading-6 text-white/65">Sign in to sync your settings, join chat, rate presentations, and request presenter access across devices.</p>' +
      '<div class="mt-4 grid gap-2 sm:grid-cols-2">' +
      '<button type="button" class="rf-chat-login-google rf-settings-signin-google inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-white/90 transition hover:bg-white/10 active:translate-y-px">' +
      GOOGLE_BTN_SVG +
      ' Sign in with Google</button>' +
      '<button type="button" class="rf-chat-login-github rf-settings-signin-github inline-flex items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-white/90 transition hover:bg-white/10 active:translate-y-px">' +
      GITHUB_BTN_SVG +
      ' Sign in with GitHub</button>' +
      '</div></div>' +
      '<div class="mt-4 rounded-2xl border border-white/10 bg-white/[0.02] p-4">' +
      '<h3 class="text-sm font-semibold text-white/90">Privacy at a glance</h3>' +
      '<div class="mt-3 grid gap-2 text-sm leading-6 text-white/65">' +
      '<p>Lectura stores your profile, settings, ratings, chat activity, and presentation activity needed to provide these features.</p>' +
      '<p>If you remove your account, your personal profile data is deleted and your past chat messages are anonymized.</p>' +
      '<p>Privacy or data request? Contact <a class="underline text-amber-200/80 hover:text-amber-200" href="mailto:hello@lectura.be">hello@lectura.be</a>.</p>' +
      '</div></div>';
    const g = container.querySelector('.rf-settings-signin-google');
    const h = container.querySelector('.rf-settings-signin-github');
    g?.addEventListener('click', () => {
      Promise.resolve(authService.signInGoogle?.()).catch((err: Error) => alert(err?.message || 'Google sign-in failed.'));
    });
    h?.addEventListener('click', () => {
      Promise.resolve(authService.signInGithub?.()).catch((err: Error) => alert(err?.message || 'GitHub sign-in failed.'));
    });
    return;
  }

  const providers = user.providerData ?? [];
  const linkedIds: Record<string, { providerId?: string; email?: string }> = {};
  providers.forEach((p) => {
    if (p.providerId) linkedIds[p.providerId] = p;
  });
  const known = [
    { id: 'google.com', label: 'Google', icon: GOOGLE_ICON },
    { id: 'github.com', label: 'GitHub', icon: GITHUB_ICON }
  ];

  container.innerHTML =
    '<div class="rounded-2xl border border-white/10 bg-white/[0.02] p-4">' +
    '<p class="text-sm font-semibold text-white/90">Linked sign-in methods</p>' +
    '<div class="mt-3 grid gap-2">' +
    known
      .map((kp) => {
        const info = linkedIds[kp.id];
        const isLinked = !!info;
        const email = info?.email ? ' · ' + escapeHtml(info.email) : '';
        return (
          '<div class="flex min-w-0 items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2">' +
          '<span class="flex min-w-0 flex-1 items-center gap-2">' +
          '<span class="h-9 w-9 shrink-0 rounded-lg border border-white/10 bg-white/5 grid place-items-center">' +
          kp.icon +
          '</span>' +
          '<span class="min-w-0 flex-1 overflow-hidden">' +
          '<span class="block truncate text-sm font-semibold text-white/85">' +
          kp.label +
          email +
          '</span>' +
          '</span>' +
          '</span>' +
          '<span class="shrink-0 text-[0.7rem] font-semibold uppercase tracking-[0.14em] ' +
          (isLinked ? 'text-amber-100' : 'text-white/45') +
          '">' +
          (isLinked ? 'LINKED' : 'Not linked') +
          '</span>' +
          '</div>'
        );
      })
      .join('') +
    '</div></div>' +
    '<div class="mt-4 rounded-2xl border border-white/10 bg-white/[0.02] p-4">' +
    '<h3 class="text-sm font-semibold text-white/90">Your data in Lectura</h3>' +
    '<div class="mt-3 grid gap-2 text-sm leading-6 text-white/65">' +
    '<p>Your account can store profile details, saved settings, visited presentations, ratings, chat participation, and presenter access requests.</p>' +
    '<p>Presentation analytics are surfaced to presenters in aggregate, while your account page keeps your own synced history and preferences.</p>' +
    '<p>If you remove your account, Lectura deletes your personal profile data and anonymizes your old chat messages as <strong class="text-white/85">Former participant</strong>.</p>' +
    '<p>Privacy or GDPR request? Contact <a class="underline text-amber-200/80 hover:text-amber-200" href="mailto:hello@lectura.be">hello@lectura.be</a>.</p>' +
    '</div></div>' +
    '<div class="mt-4 grid gap-2 sm:grid-cols-2">' +
    '<button type="button" class="rf-profile-signout inline-flex items-center justify-center rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-white/90 transition hover:bg-white/10 active:translate-y-px">Sign out</button>' +
    '<button type="button" class="rf-profile-remove-account inline-flex items-center justify-center rounded-xl border border-red-400/20 bg-red-400/10 px-4 py-3 text-sm font-semibold text-red-100 transition hover:bg-red-400/15 active:translate-y-px">Remove account &amp; data</button>' +
    '</div>';

  const signoutBtn = container.querySelector('.rf-profile-signout');
  const removeBtn = container.querySelector('.rf-profile-remove-account');
  signoutBtn?.addEventListener('click', () => {
    Promise.resolve(authService.signOut?.()).catch((err: Error) => alert(err?.message || 'Sign out failed.'));
  });
  removeBtn?.addEventListener('click', () => {
    if (!confirm('Remove your account and all associated data? This cannot be undone.')) return;
    const reauth = authService.reauthenticateCurrentUser;
    if (typeof reauth !== 'function') {
      alert('Remove account is not available for this sign-in method.');
      return;
    }
    const deleteUser = authService.deleteCurrentUser;
    if (typeof deleteUser !== 'function') {
      alert('Account deletion is not available for this sign-in method.');
      return;
    }
    reauth.call(authService).then(
      () => {
        // First delete auth account, then close settings.
        deleteUser.call(authService).then(
          () => onClose(),
          (err: Error) => alert(err?.message || 'Account deletion failed.')
        );
      },
      (err: Error) => alert(err?.message || 'Re-authentication failed. Sign in again and retry.')
    );
  });
}

/**
 * Build the settings block (TTS + appearance) for the legacy profile modal.
 */
export function buildSettingsBlock(): HTMLElement {
  const wrap = createEl('div', { className: 'rf-profile-modal' });
  const block = createEl('div', { className: 'rf-settings-block' });

  const ttsHeading = createEl('div', { className: 'rf-settings-heading' });
  ttsHeading.textContent = 'Text to speech';
  block.appendChild(ttsHeading);

  const langRow = createEl('div', { className: 'rf-settings-row' });
  langRow.innerHTML = '<label class="rf-settings-label" for="rf-settings-lang">Language</label>';
  const langSelect = createEl('select', { className: 'rf-settings-select', id: 'rf-settings-lang' });
  TTS_LANGS.forEach(({ value, label }) => {
    const opt = document.createElement('option');
    opt.value = value;
    opt.textContent = label;
    langSelect.appendChild(opt);
  });
  langRow.appendChild(langSelect);
  block.appendChild(langRow);

  const voiceRow = createEl('div', { className: 'rf-settings-row' });
  voiceRow.innerHTML = '<label class="rf-settings-label" for="rf-settings-voice">Voice</label>';
  const voiceSelect = createEl('select', { className: 'rf-settings-select', id: 'rf-settings-voice' });
  voiceSelect.innerHTML = '<option value="">Default</option>';
  voiceRow.appendChild(voiceSelect);
  block.appendChild(voiceRow);

  function fillVoices(): void {
    const voices = typeof speechSynthesis !== 'undefined' ? speechSynthesis.getVoices() : [];
    const current = (getSetting('tts', 'voice') as string) ?? '';
    const first = voiceSelect.querySelector('option');
    voiceSelect.innerHTML = '';
    if (first) voiceSelect.appendChild(first);
    voices.forEach((v) => {
      const opt = document.createElement('option');
      opt.value = v.name;
      opt.textContent = v.name + (v.lang ? ` (${v.lang})` : '');
      if (v.name === current) opt.selected = true;
      voiceSelect.appendChild(opt);
    });
  }
  fillVoices();
  if (typeof speechSynthesis !== 'undefined' && speechSynthesis.onvoiceschanged) {
    speechSynthesis.onvoiceschanged = fillVoices;
  }

  const rateRow = createEl('div', { className: 'rf-settings-row' });
  rateRow.innerHTML = '<label class="rf-settings-label" for="rf-settings-rate">Speed</label>';
  const rateVal = createEl('span', { className: 'rf-settings-value' });
  const rate = (getSetting('tts', 'rate') as number) ?? 0.9;
  rateVal.textContent = String(rate);
  const rateInput = createEl('input', {
    type: 'range',
    className: 'rf-settings-range',
    id: 'rf-settings-rate',
    min: '0.5',
    max: '1.5',
    step: '0.1',
    value: String(rate)
  });
  rateInput.addEventListener('input', () => {
    const v = parseFloat((rateInput as HTMLInputElement).value);
    setSetting('tts', 'rate', v);
    rateVal.textContent = String(v);
  });
  rateRow.appendChild(rateVal);
  rateRow.appendChild(rateInput);
  block.appendChild(rateRow);

  const volumeRow = createEl('div', { className: 'rf-settings-row' });
  volumeRow.innerHTML = '<label class="rf-settings-label" for="rf-settings-volume">Volume</label>';
  const volumeVal = createEl('span', { className: 'rf-settings-value' });
  const volume = (getSetting('tts', 'volume') as number) ?? 1;
  volumeVal.textContent = String(volume);
  const volumeInput = createEl('input', {
    type: 'range',
    className: 'rf-settings-range',
    id: 'rf-settings-volume',
    min: '0',
    max: '1',
    step: '0.1',
    value: String(volume)
  });
  volumeInput.addEventListener('input', () => {
    const v = parseFloat((volumeInput as HTMLInputElement).value);
    setSetting('tts', 'volume', v);
    volumeVal.textContent = String(v);
  });
  volumeRow.appendChild(volumeVal);
  volumeRow.appendChild(volumeInput);
  block.appendChild(volumeRow);

  const subtitlesRow = createEl('div', { className: 'rf-settings-row rf-settings-row-check' });
  const subtitlesCheck = createEl('input', {
    type: 'checkbox',
    className: 'rf-settings-checkbox',
    id: 'rf-settings-subtitles'
  }) as HTMLInputElement;
  subtitlesCheck.checked = (getSetting('tts', 'subtitles') as boolean) ?? false;
  subtitlesCheck.addEventListener('change', () => setSetting('tts', 'subtitles', subtitlesCheck.checked));
  subtitlesRow.appendChild(subtitlesCheck);
  const subtitlesLabel = createEl('label', { className: 'rf-settings-label', for: 'rf-settings-subtitles' });
  subtitlesLabel.textContent = 'Subtitles';
  subtitlesRow.appendChild(subtitlesLabel);
  block.appendChild(subtitlesRow);

  const autoAdvanceRow = createEl('div', { className: 'rf-settings-row rf-settings-row-check' });
  const autoAdvanceCheck = createEl('input', {
    type: 'checkbox',
    className: 'rf-settings-checkbox',
    id: 'rf-settings-auto-advance'
  }) as HTMLInputElement;
  autoAdvanceCheck.checked = (getSetting('tts', 'autoAdvance') as boolean) ?? false;
  autoAdvanceCheck.addEventListener('change', () => setSetting('tts', 'autoAdvance', autoAdvanceCheck.checked));
  autoAdvanceRow.appendChild(autoAdvanceCheck);
  const autoAdvanceLabel = createEl('label', { className: 'rf-settings-label', for: 'rf-settings-auto-advance' });
  autoAdvanceLabel.textContent = 'Auto-advance slides';
  autoAdvanceRow.appendChild(autoAdvanceLabel);
  block.appendChild(autoAdvanceRow);

  const appearanceHeading = createEl('div', { className: 'rf-settings-heading' });
  appearanceHeading.textContent = 'Appearance';
  block.appendChild(appearanceHeading);

  const colorRow = createEl('div', { className: 'rf-settings-row' });
  const colorLabel = createEl('div', { className: 'rf-settings-label' });
  colorLabel.textContent = 'Highlight color';
  colorRow.appendChild(colorLabel);
  const swatches = createEl('div', { className: 'rf-settings-swatches' });
  const currentColorId = (getSetting('appearance', 'highlightColorId') as string) ?? 'purple';
  HIGHLIGHT_COLORS.forEach((c) => {
    const btn = createEl('button', {
      type: 'button',
      className: 'rf-settings-swatch' + (c.id === currentColorId ? ' rf-settings-swatch-active' : ''),
      'aria-label': c.label,
      title: c.label
    });
    btn.style.backgroundColor = `rgb(${c.rgb})`;
    btn.addEventListener('click', () => {
      setSetting('tts', 'highlightColorId', c.id);
      setSetting('appearance', 'highlightColorId', c.id);
      applyHighlightColorToDOM(c.rgb);
      swatches.querySelectorAll('.rf-settings-swatch').forEach((s) => s.classList.remove('rf-settings-swatch-active'));
      btn.classList.add('rf-settings-swatch-active');
    });
    swatches.appendChild(btn);
  });
  colorRow.appendChild(swatches);
  block.appendChild(colorRow);

  langSelect.value = (getSetting('tts', 'lang') as string) ?? 'en';
  langSelect.addEventListener('change', () => {
    const newLang = langSelect.value;
    setSetting('tts', 'lang', newLang);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('lectura-tts-lang-changed', { detail: { lang: newLang } }));
    }
  });

  voiceSelect.addEventListener('change', () => {
    setSetting('tts', 'voice', voiceSelect.value || null);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('lectura-tts-voice-changed'));
    }
  });

  wrap.appendChild(block);
  return wrap;
}
